import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

/** LNK — THE ONE WAY TO DRAW A LINK.
 *
 *  Before this, 43 link-ish elements across the dashboard carried 32 DISTINCT className strings,
 *  and eight of them had no link affordance at rest at all -- `hover:text-accent` and nothing
 *  else, so the thing looked exactly like the plain text beside it until your cursor happened to
 *  cross it. On Integrity three of those sat in a row next to non-link siblings styled the same
 *  way. There is no `a {}` rule in the dashboard's CSS, so an anchor with no accent class inherits
 *  Tailwind preflight's `color: inherit; text-decoration: inherit` and is, literally, invisible as
 *  a link.
 *
 *  ONE COMPONENT, THREE ELEMENTS. `to` renders a react-router <Link>, `href` an <a>, and neither
 *  (with onClick) a <button>. That is the part that actually kills the 32 strings: an in-page
 *  action that reads as a link -- "Put back", "Use this again", "Edit" -- was being hand-styled to
 *  match a <Link> somewhere else, and the two drifted because nothing tied them together. Now the
 *  same thing is spelled the same way whatever element carries it. It stays a <button> when it is
 *  an action, so it is still announced as a control and still reachable by keyboard.
 *
 *  AFFORDANCE AT REST IS COLOUR; HOVER ADDS THE UNDERLINE. Underlining every link at rest was the
 *  other option and it is the more accessible one, but it would restyle several hundred rows of
 *  dense table text at once, which is a redesign rather than a fix. Colour at rest plus underline
 *  on hover is the floor this sets: no link is indistinguishable from the text beside it.
 *
 *  SIZE AND WEIGHT ARE NOT PROPS. They come from the call site's own className, because a link in
 *  a table row and a link under a card genuinely differ, and a `size` enum would just move the
 *  variance somewhere tidier. What this fixes is the part that should never have varied: the
 *  colour, the hover, the focus ring and the disabled treatment.
 *
 *  WHY IT IS NOT IN ui.tsx. CI keeps dashboard/src/components/ui.tsx and the portal's copy
 *  byte-identical, and the portal does not depend on react-router -- importing RouterLink there
 *  would fail its build. The portal is a plain-CSS app with two anchors in it; it gets the same
 *  floor from an `a` rule in its own stylesheet instead.
 */
export function Lnk({ to, href, tone = "accent", className = "", children, ...p }: {
  to?: string; href?: string; tone?: "accent" | "danger" | "quiet";
  className?: string; children?: ReactNode; [k: string]: any;
}) {
  const TONE = {
    accent: "text-accent hover:underline",
    // Where the link IS the bad news -- a flagged walkbook, a suspect pass. Red at rest for the
    // same reason accent is: it has to read as a link before you touch it.
    danger: "text-bad hover:underline",
    // The footnote affordance: a dotted underline on muted text, for "why?" and "what does this
    // mean" disclosures that should not compete with the accent links around them.
    quiet: "text-muted underline decoration-dotted hover:text-text",
  }[tone];
  const cls = `${TONE} underline-offset-2 cursor-pointer disabled:opacity-60 disabled:cursor-default ` +
    `focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent rounded-sm ${className}`;
  if (to) return <RouterLink to={to} className={cls} {...p}>{children}</RouterLink>;
  if (href) return <a href={href} className={cls} {...p}>{children}</a>;
  return <button type="button" className={cls} {...p}>{children}</button>;
}
